import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { JoinButton } from '../../../../components';
import css from './CrewCard.module.css';

const formatCategory = category =>
  category
    ? String(category)
        .trim()
        .toUpperCase()
    : 'GENERAL';

const formatFans = fans => {
  const fanText = fans === undefined || fans === null || fans === '' ? '0' : String(fans);
  return `${fanText} fans`;
};

const CrewCard = ({
  crew,
  onClick = () => {},
  onJoinCrew = () => {},
  isJoined = false,
  theme = 'light',
  joinCrewButtonClassName = null,
  showFans = true,
  joinText = 'Join Crew',
  joinedText = 'Take me there',
  joinDisabled = false,
}) => {
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const handleCardClick = () => {
    if (onClick) {
      onClick(crew);
    }
  };

  const handleJoinClick = e => {
    e.stopPropagation(); // Prevent card click
    if (onJoinCrew) {
      onJoinCrew(crew.id);
    }
  };

  const handleImageLoad = () => {
    setImageReady(true);
  };

  const handleImageError = () => {
    setImageFailed(true);
    setImageReady(false);
  };
  const description =
    crew.description ||
    'Join this Crew to connect with fans, share plans and discover what is happening next.';

  return (
    <div
      className={classNames(css.crewCard, { [css.dark]: theme === 'dark' })}
      onClick={handleCardClick}
    >
      <div className={css.crewTopRow}>
        <div className={css.crewAvatar}>
          <div className={css.crewImagePlaceholder} aria-hidden="true" />
          {crew.image ? (
            <img
              src={crew.image}
              alt={crew.name}
              className={`${css.crewImage} ${imageReady && !imageFailed ? css.imageReady : ''}`}
              loading="lazy"
              decoding="async"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : null}
          <div
            className={`${css.crewImageFallback} ${
              imageFailed || !crew.image ? css.fallbackVisible : ''
            }`}
            aria-hidden={crew.image && !imageFailed}
          >
            {crew.name.charAt(0)}
          </div>
        </div>
        <span className={css.categoryPill}>{formatCategory(crew.category || crew.artist)}</span>
      </div>

      <div className={css.crewInfo}>
        <h3 className={css.crewName}>{crew.name}</h3>
        {showFans ? <p className={css.crewFans}>{formatFans(crew.fans)}</p> : null}
        <p className={css.crewDescription}>{description}</p>
      </div>

      <JoinButton
        className={classNames(css.joinCrewButton, joinCrewButtonClassName)}
        isJoined={isJoined}
        joinText={joinText}
        joinedText={joinedText}
        onClick={handleJoinClick}
        disabled={joinDisabled}
        size="small"
      />
    </div>
  );
};

CrewCard.propTypes = {
  crew: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    artist: PropTypes.string,
    status: PropTypes.string,
    fans: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    category: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  onJoinCrew: PropTypes.func,
  isJoined: PropTypes.bool,
  theme: PropTypes.oneOf(['light', 'dark']),
  joinCrewButtonClassName: PropTypes.string,
  showFans: PropTypes.bool,
  joinText: PropTypes.string,
  joinedText: PropTypes.string,
  joinDisabled: PropTypes.bool,
};

export default CrewCard;
